import type { FC, CSSProperties, JSX } from 'react';
import type { RuleSet } from 'styled-components';

import styled, { css } from 'styled-components';

import { media } from '../../shared/core/styles';
import { useProductListContext } from '~/utilities/context/static/ProductListContext';
import { SiteType } from '~/utilities/graphql/codegen';

const GStarGridStyles = css`
  ${({ theme }) => css`
    --grid-row-gap: ${theme.size['6']};
    --grid-column-gap: ${theme.size['1']};
    --grid-inline-margin: 0;
    --grid-items: 2;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      --grid-row-gap: ${theme.size['8']};
      --grid-inline-margin: ${theme.size['4']};
      --grid-items: 3;
    }

    @media ${({ theme }) => media(theme).greaterThan('desktop-sm')} {
      --grid-row-gap: ${theme.size['8']};
      --grid-inline-margin: ${theme.size['6']};
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      --grid-items: 4;
    }
  `}
`;

const AafGridStyles = css`
  ${({ theme }) => css`
    --grid-row-gap: ${theme.size['4']};
    --grid-column-gap: ${theme.size['0-5']};
    --grid-items: 2;
    --grid-inline-margin: 0;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      --grid-row-gap: ${theme.size['6']};
      --grid-items: 3;
      --grid-inline-margin: 0;
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      --grid-items: 4;
    }
  `}
`;

const WahtsGridStyles = css`
  ${({ theme }) => css`
    --grid-row-gap: ${theme.size['6']};
    --grid-column-gap: ${theme.size['1']};
    --grid-inline-margin: 0;
    --grid-items: 1;

    @media ${({ theme }) => media(theme).greaterThan('sm')} {
      --grid-row-gap: ${theme.size['8']};
      --grid-inline-margin: ${theme.size['4']};
      --grid-items: 2;
    }

    @media ${({ theme }) => media(theme).greaterThan('desktop-sm')} {
      --grid-row-gap: ${theme.size['8']};
      --grid-inline-margin: ${theme.size['6']};
    }

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      --grid-items: 3;
    }
  `}
`;

const GridStylesConfig: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: GStarGridStyles,
  [SiteType.Wahts]: WahtsGridStyles,
  [SiteType.Outlet]: GStarGridStyles,
  [SiteType.Employeeshop]: GStarGridStyles,
  [SiteType.Aaf]: AafGridStyles,
};

const S = {
  Grid: styled.section`
    display: grid;
    row-gap: var(--grid-row-gap);
    column-gap: var(--grid-column-gap);
    margin-inline: var(--grid-inline-margin);
    grid-template-columns: repeat(var(--grid-items), minmax(0, 1fr));

    & > * {
      width: 100%;

      @media ${({ theme }) => media(theme).noMotionPreference} {
        will-change: height;
        transition: height 0.25s ease-in-out;
      }
    }

    ${({ theme }) => GridStylesConfig[theme.siteType]}
  `,
};

export const ProductGrid: FC<JSX.IntrinsicElements['div']> = ({ style, ...rest }) => {
  const { columns } = useProductListContext();

  const elementStyle: CSSProperties | undefined = columns ? { '--grid-items': columns } : undefined;

  return <S.Grid style={{ ...elementStyle, ...style }} {...rest} />;
};
